import { getResultAnalysisKeys, selectOrderResult, selectResultSection, selectedResultMeasurements } from "./resultOrderSelection";
import { useAppDispatch } from "../../../app/hooks";
import { requestOiTreeRefresh } from "../../../Slices/OiTreeSlice";
import OiLoadingSpinner from "../OiLoadingSpinner";
import OiPageHeader, { OiHeaderButton } from "../OiPageHeader";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle } from "@fluentui/react-components";
import { ChevronDown20Regular, ChevronRight20Regular, DataTrending24Regular, Print20Regular, Save20Regular } from "@fluentui/react-icons";
import { useNavigate, useParams } from "react-router-dom";
import { email } from "../../../App";
import { useToast } from "../../ToastProvider";
import { FileReference } from "../OiReport";
import { forgetOiLastRoute } from "../lastRoute";
import { AnalysisOrder } from "../redux/analysisOrder";
import { GetOiAnalysisOrder } from "../../../services/AnalysisOrderService";
import ElementPickerModal from "../PowderOrder/ElementPickerModal";
import {
  AnalysisResultData,
  AnalysisResultSection,
  AnalysisResultSectionRow,
  AnalysisResultStatus,
  ANALYSIS_RESULT_SAVE_ERROR,
  DownloadAnalysisResultPdf,
  emptyAnalysisResultData,
  GetOiAnalysisResult,
  GetOiAnalysisResultByAnalysisOrderUuid,
  normalizeAnalysisResultStatus,
  OpenOiAnalysisResult,
  UpdateOiAnalysisResult,
} from "../../../services/AnalysisResultService";
import { Element, GetElements } from "../../../services/PowderFormService";
import { getAnalysisDefinition, isCurrentAnalysisKey } from "./analysisConfig";
import { buildResultPayload } from "./resultPayload";
import { SampleAnalysisHeader } from "./AnalysisResultComponents/SampleAnalysisContent";
import { AnalysisSectionContent } from "./AnalysisResultComponents/AnalysisSectionContent";
import { OrderSummaryAside } from "./AnalysisResultComponents/OrderSummaryAside";
import { ResultsSummarySection } from "./AnalysisResultComponents/ResultsSummarySection";
import { PrintResultDialog } from "./AnalysisResultComponents/PrintResultDialog";
import {
  applySectionEditRules,
  containerClassName,
  formatShortDate,
  getSectionPresentation,
  SectionStatusPill,
} from "./AnalysisResultComponents/shared";
import { areAllSectionsComplete, normalizeSamples } from "./utils";

interface AnalysisResultViewProps {
  onOpenFileGallery?: (files: FileReference[], initialIndex: number) => void;
}

const DEFAULT_CHEMISTRY_ROWS = ["Ctot", "Cfree", "O", "N", "Co", "Cr", "Fe", "Mo", "Nb", "Ni", "Ta", "Ti", "V", "W", "Zr"];

async function uploadFiles(
  files: File[],
  showToast: ReturnType<typeof useToast>["showToast"],
): Promise<FileReference[]> {
  if (!files || files.length === 0) {
    return [];
  }

  const { UploadFile } = await import("../../../services/OiService");
  const uploadResults = await Promise.all(files.map(async file => {
    try {
      const fileId = await UploadFile(file);
      const cleanFileId = fileId ? fileId.toString().replace(/^["']|["']$/g, "") : null;
      return cleanFileId ? { uuid: cleanFileId, fileName: file.name } : null;
    } catch (error) {
      console.error(`Failed to upload ${file.name}:`, error);
      showToast({ title: "Upload failed", intent: "error", body: `Failed to upload ${file.name}` });
      return null;
    }
  }));

  const successfulUploads = uploadResults.filter((upload): upload is FileReference => upload !== null);

  if (successfulUploads.length === 0) {
    showToast({ title: "Upload failed", intent: "error", body: "No files were uploaded." });
  }

  return successfulUploads;
}

function parseOpenedUuid(response: string | null): string | undefined {
  try {
    return (JSON.parse(response || "{}") as { uuid?: string }).uuid;
  } catch {
    return undefined;
  }
}

function buildSummaryRows(order: AnalysisOrder) {
  return normalizeSamples(order.linkedPowderOrderLabel || order.linkedPowderOrder, order.samples).map(sample => ({
    sampleId: sample.sampleId,
    density: "",
    hc: "",
    co: sample.coPercentage?.toString() ?? "",
    o: "",
    coM: "",
    mm: "",
    mi: "",
    ma: "",
    gradient: "",
  }));
}

function buildChemistryRows(sampleIds: string[]) {
  return DEFAULT_CHEMISTRY_ROWS.map(rowLabel => ({
    rowLabel,
    sampleValues: Object.fromEntries(sampleIds.map(sampleId => [sampleId, ""])),
  }));
}

function buildSectionRows(analysisKey: string, sampleIds: string[]): AnalysisResultSectionRow[] {
  const emptySampleValues = Object.fromEntries(sampleIds.map(sampleId => [sampleId, ""]));

  switch (analysisKey) {
    case "carbon":
      return ["Ctot", "Cfree"].map(rowLabel => ({ rowLabel, sampleValues: { ...emptySampleValues } }));
    case "hardness":
      return ["Hv10", "Hv30", "Hv3", "K1C"].map(rowLabel => ({ rowLabel, sampleValues: { ...emptySampleValues } }));
    case "xrf":
      return ["Co", "Cr", "Fe", "Mo", "Nb", "Ni", "Ta", "Ti", "V", "W", "Zr"].map(rowLabel => ({ rowLabel, sampleValues: { ...emptySampleValues } }));
    case "hcMmDensity":
      return ["Hc", "σs", "MM", "Density"].map(rowLabel => ({ rowLabel, sampleValues: { ...emptySampleValues } }));
    default:
      return [];
  }
}

function buildSections(order: AnalysisOrder): AnalysisResultSection[] {
  const sampleIds = normalizeSamples(order.linkedPowderOrderLabel || order.linkedPowderOrder, order.samples).map(sample => sample.sampleId);
  const effectiveKeys = getResultAnalysisKeys(order);

  return effectiveKeys.map(analysisKey => {
    const definition = getAnalysisDefinition(analysisKey);
    return {
      analysisKey,
      title: definition?.title ?? analysisKey,
      status: AnalysisResultStatus.InProgress,
      startedBy: "",
      startedDate: "",
      rows: selectedResultMeasurements(order, analysisKey)?.map(rowLabel => ({ rowLabel, sampleValues: Object.fromEntries(sampleIds.map(id => [id, ""])) })) ?? buildSectionRows(analysisKey, sampleIds),
      files: [],
      comment: "",
      completion: {
        isComplete: false,
        completedBy: "",
        completedDate: "",
      },
    };
  });
}

function createInitialResultData(order: AnalysisOrder): AnalysisResultData {
  const sampleIds = normalizeSamples(order.linkedPowderOrderLabel || order.linkedPowderOrder, order.samples).map(sample => sample.sampleId);
  return {
    ...emptyAnalysisResultData(),
    summaryRows: buildSummaryRows(order),
    chemistryRows: buildChemistryRows(sampleIds),
    sections: buildSections(order),
  };
}

export default function AnalysisResultView({ onOpenFileGallery }: AnalysisResultViewProps) {
  const { uuid, analysisOrderUuid } = useParams<{ uuid?: string; analysisOrderUuid?: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const dispatch = useAppDispatch();
  const isDirtyRef = useRef(false);
  const editVersionRef = useRef(0);
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [order, setOrder] = useState<AnalysisOrder | null>(null);
  const [orderId, setOrderId] = useState("");
  const [linkedAnalysisOrderUuid, setLinkedAnalysisOrderUuid] = useState("");
  const [resultUuid, setResultUuid] = useState("");
  const [resultCreatedAt, setResultCreatedAt] = useState("");
  const [resultLastUpdated, setResultLastUpdated] = useState("");
  const [status, setStatus] = useState<AnalysisResultStatus>(AnalysisResultStatus.InProgress);
  const [rawResultData, setRawResultData] = useState<AnalysisResultData>(emptyAnalysisResultData());
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [summaryExpanded, setSummaryExpanded] = useState(true);
  const [allElements, setAllElements] = useState<Element[]>([]);
  const [elementPickerOpen, setElementPickerOpen] = useState(false);
  const [elementSearch, setElementSearch] = useState("");
  const [uploadingSectionKey, setUploadingSectionKey] = useState<string | null>(null);
  const [, setDragOverSectionKey] = useState<string | null>(null);
  const sectionFileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  // A completed result is final: it is shown read-only and can only be printed.
  const isLocked = status === AnalysisResultStatus.Completed;
  const isLockedRef = useRef(isLocked);
  isLockedRef.current = isLocked;

  const updateResultData = useCallback((updater: (current: AnalysisResultData) => AnalysisResultData) => {
    if (isLockedRef.current) return;
    isDirtyRef.current = true;
    editVersionRef.current += 1;
    setRawResultData(updater);
  }, []);

  const resultData = useMemo(() => order
    ? selectOrderResult(order, rawResultData, buildSections(order), normalizeSamples(order.linkedPowderOrderLabel || order.linkedPowderOrder, order.samples).map(sample => sample.sampleId))
    : rawResultData, [order, rawResultData]);

  useEffect(() => {
    void (async () => {
      const list = await GetElements();
      if (list) {
        setAllElements(list);
      }
    })();
  }, []);

  const loadResult = useCallback(async () => {
    // Opening another result must not count as an edit of it.
    isDirtyRef.current = false;
    setLoading(true);
    try {
      if (analysisOrderUuid) {
        const orderResponse = await GetOiAnalysisOrder(analysisOrderUuid);
        if (!orderResponse) {
          return;
        }

        const parsedOrder = JSON.parse(orderResponse.jsonData) as AnalysisOrder;
        setOrder(parsedOrder);
        setOrderId(orderResponse.orderId || "");
        setLinkedAnalysisOrderUuid(analysisOrderUuid);

        const existing = await GetOiAnalysisResultByAnalysisOrderUuid(analysisOrderUuid);
        if (existing) {
          setResultUuid(existing.uuid);
          setResultCreatedAt(existing.createdAt || "");
          setResultLastUpdated(existing.lastUpdated || "");
          setStatus(normalizeAnalysisResultStatus(existing.status));
          setRawResultData(JSON.parse(existing.jsonData) as AnalysisResultData);
        } else {
          // The result is only created once someone starts editing it.
          setResultUuid("");
          setStatus(AnalysisResultStatus.NotStarted);
          setRawResultData(createInitialResultData(parsedOrder));
        }

        return;
      }

      if (!uuid) {
        return;
      }

      const response = await GetOiAnalysisResult(uuid);
      if (!response) {
        forgetOiLastRoute(`/Oi/analysisResult/${uuid}`);
        return;
      }

      setResultUuid(response.uuid);
      setResultCreatedAt(response.createdAt || "");
      setResultLastUpdated(response.lastUpdated || "");
      setStatus(normalizeAnalysisResultStatus(response.status));
      setRawResultData(JSON.parse(response.jsonData) as AnalysisResultData);
      setLinkedAnalysisOrderUuid(response.analysisOrderUuid);

      const orderResponse = await GetOiAnalysisOrder(response.analysisOrderUuid);
      if (orderResponse) {
        const parsedOrder = JSON.parse(orderResponse.jsonData) as AnalysisOrder;
        setOrder(parsedOrder);
        setOrderId(orderResponse.orderId || "");
      }
    } catch (error) {
      console.error("Failed to load analysis result", error);
    } finally {
      setLoading(false);
    }
  }, [analysisOrderUuid, uuid]);

  useEffect(() => {
    void loadResult();
  }, [loadResult]);

  useEffect(() => {
    setExpandedSections(current => {
      const next = { ...current };
      resultData.sections.forEach(section => {
        if (next[section.analysisKey] === undefined) {
          next[section.analysisKey] = !section.completion.isComplete && section.analysisKey !== "xrd";
        }
      });
      return next;
    });
  }, [resultData.sections]);

  const normalizedSamples = useMemo(() => order ? normalizeSamples(order.linkedPowderOrderLabel || order.linkedPowderOrder, order.samples) : [], [order]);
  const chemistrySummaryRows = useMemo(() => {
    const carbonSection = resultData.sections.find(section => section.analysisKey === "carbon");
    const xrfSection = resultData.sections.find(section => section.analysisKey === "xrf");
    const oxygenNitrogenSection = resultData.sections.find(section => section.analysisKey === "oxygenNitrogen");
    const chemistryRowOrder = [
      ...DEFAULT_CHEMISTRY_ROWS,
      ...resultData.chemistryRows
        .map(row => row.rowLabel)
        .filter(rowLabel => !DEFAULT_CHEMISTRY_ROWS.includes(rowLabel) && rowLabel !== "Ctot" && rowLabel !== "Cfree"),
      ...(xrfSection?.rows ?? [])
        .map(row => row.rowLabel)
        .filter(rowLabel => !DEFAULT_CHEMISTRY_ROWS.includes(rowLabel)),
    ].filter((rowLabel, index, rows) => rows.indexOf(rowLabel) === index);

    return chemistryRowOrder.map(rowLabel => {
      const sourceRow = rowLabel === "Ctot" || rowLabel === "Cfree"
        ? carbonSection?.rows.find(row => row.rowLabel === rowLabel)
        : (rowLabel === "O" || rowLabel === "N" ? oxygenNitrogenSection?.rows.find(row => row.rowLabel === rowLabel) : undefined) ?? xrfSection?.rows.find(row => row.rowLabel === rowLabel);
      const fallbackRow = resultData.chemistryRows.find(row => row.rowLabel === rowLabel);

      return {
        rowLabel,
        sampleValues: Object.fromEntries(normalizedSamples.map(sample => [
          sample.sampleId,
          sourceRow?.sampleValues[sample.sampleId] ?? fallbackRow?.sampleValues[sample.sampleId] ?? "",
        ])),
      };
    });
  }, [normalizedSamples, resultData.chemistryRows, resultData.sections]);
  const xrfSelectedElements = useMemo(() => resultData.sections.find(section => section.analysisKey === "xrf")?.rows.map(row => row.rowLabel) ?? [], [resultData.sections]);
  const allSectionsComplete = areAllSectionsComplete(resultData.sections);
  const canCompleteResult = status === AnalysisResultStatus.InProgress && Boolean(resultUuid) && allSectionsComplete;

  const persist = useCallback(async (nextStatus = status, silent = false) => {
    const payload = buildResultPayload(rawResultData, resultData);
    const savedVersion = editVersionRef.current;
    // Edits made while the request was in flight keep the result dirty.
    const markSaved = () => {
      if (editVersionRef.current === savedVersion) {
        isDirtyRef.current = false;
      }
    };

    if (!resultUuid) {
      if (!linkedAnalysisOrderUuid) {
        return;
      }

      const opened = await OpenOiAnalysisResult(linkedAnalysisOrderUuid, payload, email);
      const openedUuid = parseOpenedUuid(opened.uuid);
      if (openedUuid) {
        markSaved();
        setResultUuid(openedUuid);
        setStatus(AnalysisResultStatus.InProgress);
        dispatch(requestOiTreeRefresh());
      } else {
        showToast({ title: "Save failed", intent: "error", body: opened.error ?? ANALYSIS_RESULT_SAVE_ERROR });
      }
      return;
    }

    // Editing a completed result reopens it.
    const finalStatus = nextStatus === AnalysisResultStatus.Completed && !allSectionsComplete ? AnalysisResultStatus.InProgress : nextStatus;
    const outcome = await UpdateOiAnalysisResult(resultUuid, payload, finalStatus);
    if (outcome.ok) {
      markSaved();
      if (finalStatus !== status) {
        dispatch(requestOiTreeRefresh());
      }
      setStatus(finalStatus);
      if (!silent) {
        showToast({ title: "Analysis result saved", intent: "success", body: orderId ? `${orderId} updated.` : "Result updated." });
      }
    } else {
      showToast({ title: "Save failed", intent: "error", body: outcome.error ?? ANALYSIS_RESULT_SAVE_ERROR });
    }
  }, [allSectionsComplete, dispatch, linkedAnalysisOrderUuid, orderId, rawResultData, resultData, resultUuid, showToast, status]);

  useEffect(() => {
    if (loading || !(resultUuid || linkedAnalysisOrderUuid) || !isDirtyRef.current) {
      return;
    }

    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      void persist(status, true);
    }, 1000);

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [linkedAnalysisOrderUuid, loading, persist, resultData, resultUuid, status]);

  useEffect(() => () => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
  }, []);

  const updateSectionRowCell = (analysisKey: string, rowIndex: number, sampleId: string, value: string) => {
    const rowLabel = resultData.sections.find(section => section.analysisKey === analysisKey)?.rows[rowIndex]?.rowLabel;
    updateSection(analysisKey, section => ({ ...section, status: AnalysisResultStatus.InProgress, rows: section.rows.map(row => row.rowLabel === rowLabel ? { ...row, sampleValues: { ...row.sampleValues, [sampleId]: value } } : row) }));
  };

  const updateSection = (analysisKey: string, updater: (section: AnalysisResultSection) => AnalysisResultSection) => {
    if (!order) return;
    updateResultData(current => {
      const existing = current.sections.find(section => section.analysisKey === analysisKey);
      const fallback = existing ?? buildSections(order).find(section => section.analysisKey === analysisKey);
      if (!fallback) return current;
      const visible = selectResultSection(order, fallback, normalizedSamples.map(sample => sample.sampleId));
      const updated = applySectionEditRules(visible, updater(visible), email);
      const selected = selectedResultMeasurements(order, analysisKey);
      const hiddenRows = selected === null ? [] : fallback.rows.filter(row => !selected.includes(row.rowLabel));
      const next = { ...updated, rows: [...updated.rows, ...hiddenRows] };
      return { ...current, sections: existing ? current.sections.map(section => section.analysisKey === analysisKey ? next : section) : [...current.sections, next] };
    });
  };

  const handleSectionFileUpload = async (analysisKey: string, files: File[]) => {
    if (!files || files.length === 0) {
      return;
    }

    setUploadingSectionKey(analysisKey);
    try {
      const uploadedFiles = await uploadFiles(files, showToast);

      if (uploadedFiles.length > 0) {
        updateSection(analysisKey, current => ({
          ...current,
          status: AnalysisResultStatus.InProgress,
          files: [...current.files, ...uploadedFiles],
        }));

        showToast({
          title: "Upload complete",
          intent: "success",
          body: uploadedFiles.length === 1 ? uploadedFiles[0].fileName : `${uploadedFiles.length} files uploaded.`,
        });
      }
    } catch (error) {
      console.error("Failed to upload section files", error);
      showToast({ title: "Upload failed", intent: "error", body: "Failed to upload files." });
    } finally {
      setUploadingSectionKey(current => current === analysisKey ? null : current);
    }
  };

  const handleSectionFileSelect = (analysisKey: string) => {
    sectionFileInputRefs.current[analysisKey]?.click();
  };

  const handleRowFileSelect = (rowKey: string) => {
    sectionFileInputRefs.current[rowKey]?.click();
  };

  const removeSectionFile = (analysisKey: string, fileUuid: string) => {
    updateSection(analysisKey, current => ({
      ...current,
      files: current.files.filter(file => file.uuid !== fileUuid),
    }));
  };

  const handleSemRowFileUpload = async (analysisKey: string, rowLabel: string, files: File[]) => {
    if (!files.length) {
      return;
    }

    const uploadKey = `${analysisKey}:${rowLabel}`;
    setUploadingSectionKey(uploadKey);
    try {
      const uploadedFiles = await uploadFiles(files, showToast);
      if (uploadedFiles.length > 0) {
        updateSection(analysisKey, current => ({
          ...current,
          status: AnalysisResultStatus.InProgress,
          rows: current.rows.map(row => row.rowLabel === rowLabel
            ? { ...row, files: [...(row.files ?? []), ...uploadedFiles] }
            : row),
        }));

        showToast({
          title: "Upload complete",
          intent: "success",
          body: uploadedFiles.length === 1 ? uploadedFiles[0].fileName : `${uploadedFiles.length} files uploaded.`,
        });
      }
    } catch (error) {
      console.error("Failed to upload analysis row files", error);
      showToast({ title: "Upload failed", intent: "error", body: "Failed to upload files." });
    } finally {
      setUploadingSectionKey(current => current === uploadKey ? null : current);
    }
  };

  const removeSemRowFile = (analysisKey: string, rowLabel: string, fileUuid: string) => {
    updateSection(analysisKey, current => ({
      ...current,
      rows: current.rows.map(row => row.rowLabel === rowLabel
        ? { ...row, files: (row.files ?? []).filter(file => file.uuid !== fileUuid) }
        : row),
    }));
  };

  const addXrfElement = () => {
    setElementPickerOpen(true);
  };

  const toggleXrfElement = (symbol: string) => {
    const sampleValues = Object.fromEntries(normalizedSamples.map(sample => [sample.sampleId, ""]));
    const shouldKeepChemistryRow = DEFAULT_CHEMISTRY_ROWS.includes(symbol);

    updateResultData(current => {
      const xrfSection = current.sections.find(section => section.analysisKey === "xrf") ?? (order ? buildSections(order).find(section => section.analysisKey === "xrf") : undefined);
      if (!xrfSection) {
        return current;
      }

      const exists = xrfSection.rows.some(row => row.rowLabel === symbol);

      return {
        ...current,
        chemistryRows: exists
          ? current.chemistryRows.filter(row => shouldKeepChemistryRow || row.rowLabel !== symbol)
          : current.chemistryRows.some(row => row.rowLabel === symbol)
            ? current.chemistryRows
            : [...current.chemistryRows, { rowLabel: symbol, sampleValues: { ...sampleValues } }],
        sections: (current.sections.some(section => section.analysisKey === "xrf") ? current.sections : [...current.sections, xrfSection]).map(section => {
          if (section.analysisKey !== "xrf") {
            return section;
          }

          return applySectionEditRules(section, {
            ...section,
            rows: exists
              ? section.rows.filter(row => row.rowLabel !== symbol)
              : [...section.rows, { rowLabel: symbol, sampleValues: { ...sampleValues } }],
          }, email);
        }),
      };
    });
  };

  if (loading) {
    return (
      <OiLoadingSpinner />
    );
  }

  if (!order) {
    return <div className="p-6 text-primary-dark dark:text-primary-light">Analysis result could not be loaded.</div>;
  }

  return (
    <div className="min-w-0 w-full max-w-none mx-6 pb-12 text-primary-dark dark:text-primary-light">
      <OiPageHeader
        icon={<DataTrending24Regular />}
        title="Analysis result"
        id={orderId || undefined}
        createdAt={resultCreatedAt}
        updatedAt={resultLastUpdated}
        actions={<>
          <OiHeaderButton variant="secondary" onClick={() => navigate(`/Oi/analysisOrder/${linkedAnalysisOrderUuid}`)}>Back to order</OiHeaderButton>
          {isLocked ? (
            <OiHeaderButton icon={<Print20Regular />} onClick={() => setPrintDialogOpen(true)}>Print</OiHeaderButton>
          ) : (
            <OiHeaderButton icon={<Save20Regular />} disabled={!resultUuid} onClick={() => void persist()}>Save</OiHeaderButton>
          )}
          {canCompleteResult && (
            <OiHeaderButton variant="success" onClick={() => void persist(AnalysisResultStatus.Completed)}>Complete</OiHeaderButton>
          )}
        </>}
      />

      <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr),330px]">
        <div className="min-w-0 space-y-3">
          <div className="text-[14px] font-bold text-primary-dark/95 dark:text-white/95">Result</div>
          <ResultsSummarySection
            summaryExpanded={summaryExpanded}
            onToggle={() => setSummaryExpanded(current => !current)}
            resultData={resultData}
            chemistrySummaryRows={chemistrySummaryRows}
            normalizedSamples={normalizedSamples}
          />

          {resultData.sections.map(section => {
            const isExpanded = expandedSections[section.analysisKey] ?? true;
            const sectionPresentation = getSectionPresentation(section);
            return (
              <section key={section.analysisKey} className={containerClassName}>
                {isCurrentAnalysisKey(section.analysisKey) ? (
                  <SampleAnalysisHeader section={section} expanded={isExpanded} onToggle={() => setExpandedSections(current => ({ ...current, [section.analysisKey]: !isExpanded }))} />
                ) : (
                <button type="button" aria-expanded={isExpanded} className="flex w-full items-center justify-between px-3 py-2 text-left" onClick={() => setExpandedSections(current => ({ ...current, [section.analysisKey]: !isExpanded }))}>
                  <div className="flex min-w-0 flex-1 items-center justify-between gap-4 pr-4">
                    <div className="text-[14px] font-bold">{section.title}</div>
                    <div className={`text-[10px] ${sectionPresentation.tone === "done" ? "text-good-green" : "text-oi-button-blue dark:text-oi-yellow"}`}>
                      {sectionPresentation.tone === "done"
                        ? `${section.completion.completedBy ? `Completed by ${section.completion.completedBy}` : ""} ${formatShortDate(section.completion.completedDate)}`
                        : sectionPresentation.tone === "progress"
                          ? `${section.startedBy ? `started by ${section.startedBy}` : ""} ${formatShortDate(section.startedDate)}`
                          : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <SectionStatusPill label={sectionPresentation.label} tone={sectionPresentation.tone} />
                    {isExpanded ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
                  </div>
                </button>
                )}
                {isExpanded && (
                  <AnalysisSectionContent
                    section={section}
                    densityMeasurementTemperature={resultData.densityMeasurementTemperature}
                    densityCorrectionFactor={resultData.densityCorrectionFactor}
                    summaryRows={resultData.summaryRows}
                    onDensitySettingsChange={(field, value) => updateResultData(current => ({ ...current, [field]: value }))}
                    normalizedSamples={normalizedSamples}
                    uploadingSectionKey={uploadingSectionKey}
                    sectionFileInputRefs={sectionFileInputRefs}
                    onUpdateSectionRowCell={updateSectionRowCell}
                    onUpdateSection={updateSection}
                    onAddXrfElement={addXrfElement}
                    onRemoveXrfElement={toggleXrfElement}
                    onSectionFileSelect={handleSectionFileSelect}
                    onHandleSectionFileUpload={handleSectionFileUpload}
                    onHandleRowFileSelect={handleRowFileSelect}
                    onHandleSemRowFileUpload={handleSemRowFileUpload}
                    onRemoveSectionFile={removeSectionFile}
                    onRemoveSemRowFile={removeSemRowFile}
                    onSetDragOverSectionKey={setDragOverSectionKey}
                    onOpenFileGallery={onOpenFileGallery}
                    readOnly={isLocked}
                  />
                )}
              </section>
            );
          })}
        </div>

        <OrderSummaryAside
          order={order}
          orderId={orderId}
          normalizedSamples={normalizedSamples}
          resultData={resultData}
          onOpenFileGallery={onOpenFileGallery}
        />
      </div>

      <PrintResultDialog
        open={printDialogOpen}
        sections={resultData.sections}
        onClose={() => setPrintDialogOpen(false)}
        onPrint={async includeFilesForSections => {
          const ok = await DownloadAnalysisResultPdf(resultUuid, includeFilesForSections, `Analysis result - ${orderId || resultUuid}.pdf`);
          if (ok) {
            setPrintDialogOpen(false);
          } else {
            showToast({ title: "Print failed", intent: "error", body: "The PDF could not be generated." });
          }
        }}
      />

      <Dialog open={elementPickerOpen} onOpenChange={(_, data) => setElementPickerOpen(data.open)}>
        <DialogSurface className="!flex !max-h-[90dvh] !w-[min(90vw,68rem)] !max-w-[90vw] !flex-col overflow-hidden">
          <DialogBody className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <DialogTitle>Elements</DialogTitle>
            <DialogContent className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
              <ElementPickerModal
                open={elementPickerOpen}
                allElements={allElements}
                elementSearch={elementSearch}
                setElementSearch={setElementSearch}
                selectedElements={xrfSelectedElements}
                onToggleElement={toggleXrfElement}
              />
            </DialogContent>
            <DialogActions className="shrink-0 pt-3">
              <button className="bg-oi-button-blue p-2 px-6 text-primary-light" onClick={() => setElementPickerOpen(false)}>OK</button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
